import { useState, useEffect, useMemo } from 'react'
import { History, RotateCcw, Loader2, FileText, CheckCircle, GitCompare } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { templateService } from '@/lib/api/services/templates'
import type { TemplateVersion } from '@/lib/api/types'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { RestoreVersionDialog } from './RestoreVersionDialog'
import { VersionComparisonDialog } from './VersionComparisonDialog'

interface VersionHistoryTableDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  templateName: string
  currentVersionNumber?: number
  onVersionRestored?: () => void
}

export function VersionHistoryTableDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  currentVersionNumber,
  onVersionRestored,
}: VersionHistoryTableDialogProps) {
  const { toast } = useToast()
  const [versions, setVersions] = useState<TemplateVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [versionToRestore, setVersionToRestore] = useState<TemplateVersion | null>(null)
  const [selectedVersions, setSelectedVersions] = useState<string[]>([])
  const [compareDialogOpen, setCompareDialogOpen] = useState(false)

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
  } = useDataTable({
    initialState: {
      pageSize: 10,
      sortField: 'versionNumber',
      sortDirection: 'desc',
    },
  })

  useEffect(() => {
    if (open) {
      loadVersions()
    }
  }, [open, templateId])

  const loadVersions = async () => {
    try {
      setLoading(true)
      const data = await templateService.getTemplateVersions(templateId)
      setVersions(data)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load version history',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  // Client-side filtering and sorting
  const filteredAndSortedVersions = useMemo(() => {
    let result = [...versions]

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(
        (v) =>
          v.versionNumber.toString().includes(searchLower) ||
          v.changeDescription?.toLowerCase().includes(searchLower) ||
          v.createdByName?.toLowerCase().includes(searchLower) ||
          v.status?.toLowerCase().includes(searchLower) ||
          v.originalFileName?.toLowerCase().includes(searchLower)
      )
    }

    // Apply column filters
    if (filters.status?.value) {
      result = result.filter((v) => v.status === filters.status?.value)
    }

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        let aVal: unknown = a[sortField as keyof TemplateVersion]
        let bVal: unknown = b[sortField as keyof TemplateVersion]

        // Handle special cases
        if (sortField === 'versionNumber') {
          aVal = a.versionNumber
          bVal = b.versionNumber
        } else if (sortField === 'createdAt') {
          aVal = new Date(a.createdAt).getTime()
          bVal = new Date(b.createdAt).getTime()
        }

        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
        }

        const aStr = String(aVal).toLowerCase()
        const bStr = String(bVal).toLowerCase()
        return sortDirection === 'asc'
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr)
      })
    }

    return result
  }, [versions, search, filters, sortField, sortDirection])

  // Paginate results
  const paginatedVersions = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredAndSortedVersions.slice(start, start + pageSize)
  }, [filteredAndSortedVersions, page, pageSize])

  const handleRestoreClick = (version: TemplateVersion) => {
    setVersionToRestore(version)
    setRestoreDialogOpen(true)
  }

  const handleRestoreConfirm = async () => {
    if (!versionToRestore) return

    try {
      await templateService.restoreVersion(templateId, versionToRestore.id)
      toast({
        title: 'Success',
        description: `Template restored to version ${versionToRestore.versionNumber}`,
      })
      setRestoreDialogOpen(false)
      setVersionToRestore(null)
      await loadVersions()
      onVersionRestored?.()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to restore version',
        variant: 'destructive',
      })
    }
  }

  const handleVersionSelect = (versionId: string) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId)
      }
      if (prev.length >= 2 && prev[1]) {
        return [prev[1], versionId]
      }
      return [...prev, versionId]
    })
  }

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      setCompareDialogOpen(true)
    }
  }

  const columns: ColumnDef<TemplateVersion>[] = [
    {
      id: 'versionNumber',
      header: 'Version',
      accessor: 'versionNumber',
      sortable: true,
      width: '120px',
      cell: (version) => {
        const isCurrent = version.versionNumber === currentVersionNumber
        return (
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${
              isCurrent
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <FileText className={`h-4 w-4 ${
                isCurrent
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`} />
            </div>
            <span className="font-medium">v{version.versionNumber}</span>
            {isCurrent && (
              <Badge variant="success" className="text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Current
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Draft', value: 'Draft' },
        { label: 'Published', value: 'Published' },
        { label: 'Archived', value: 'Archived' },
      ],
      width: '100px',
      cell: (version) => (
        <Badge
          variant={version.status === 'Published' ? 'default' : 'secondary'}
          className="text-xs"
        >
          {version.status}
        </Badge>
      ),
    },
    {
      id: 'changeDescription',
      header: 'Description',
      accessor: 'changeDescription',
      filterable: true,
      filterType: 'text',
      cell: (version) => (
        <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px] block">
          {version.changeDescription || '-'}
        </span>
      ),
    },
    {
      id: 'createdByName',
      header: 'Created By',
      accessor: 'createdByName',
      sortable: true,
      filterable: true,
      filterType: 'text',
      width: '150px',
      cell: (version) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {version.createdByName || version.createdBy || '-'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      sortable: true,
      width: '180px',
      cell: (version) => (
        <div className="text-sm">
          <div className="text-gray-900 dark:text-white">
            {new Date(version.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(version.createdAt).toLocaleTimeString()}
          </div>
        </div>
      ),
    },
    {
      id: 'originalFileName',
      header: 'File',
      accessor: 'originalFileName',
      filterable: true,
      filterType: 'text',
      width: '150px',
      cell: (version) => (
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[140px] block">
          {version.originalFileName || '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      width: '100px',
      cell: (version) => {
        const isCurrent = version.versionNumber === currentVersionNumber
        const isSelected = selectedVersions.includes(version.id)

        return (
          <div className="flex items-center gap-1">
            <Button
              variant={isSelected ? 'default' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation()
                handleVersionSelect(version.id)
              }}
              title={isSelected ? 'Deselect for comparison' : 'Select for comparison'}
            >
              <GitCompare className="h-4 w-4" />
            </Button>
            {!isCurrent && (
              <PermissionGate permissions={['templates.update']}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRestoreClick(version)
                  }}
                  title="Restore this version"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </PermissionGate>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Version History
            </DialogTitle>
            <DialogDescription>
              View and manage versions of "{templateName}"
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {/* Compare button when 2 versions selected */}
            {selectedVersions.length === 2 && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  2 versions selected for comparison
                </span>
                <Button size="sm" onClick={handleCompare} className="gap-2">
                  <GitCompare className="h-4 w-4" />
                  Compare
                </Button>
              </div>
            )}

            {selectedVersions.length === 1 && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Select one more version to compare
                </span>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={paginatedVersions}
                totalCount={filteredAndSortedVersions.length}
                loading={false}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={setSort}
                filters={filters}
                onFilterChange={setFilters}
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search versions..."
                rowKey="id"
                emptyMessage="No versions found"
                pageSizeOptions={[5, 10, 20]}
              />
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <RestoreVersionDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        version={versionToRestore}
        onConfirm={handleRestoreConfirm}
      />

      {/* Comparison Dialog */}
      {selectedVersions.length === 2 && selectedVersions[0] && selectedVersions[1] && (
        <VersionComparisonDialog
          open={compareDialogOpen}
          onOpenChange={setCompareDialogOpen}
          templateId={templateId}
          versionId1={selectedVersions[0]}
          versionId2={selectedVersions[1]}
        />
      )}
    </>
  )
}
